import { ChangeDetectionStrategy, Component, inject, output, signal } from '@angular/core';
import { disabled, email, FormField, form, required } from '@angular/forms/signals';
import { firstValueFrom, take } from 'rxjs';
import { CurrentUserService } from '../../../../common/current-user/current-user.service';
import { FirebaseService } from '../../../../common/firebase/firebase.service';
import { SubscriptionService } from '../../../../services/subscription/subscription.service';
import { ToastNotificationService } from '../../../../atoms/toast-notification/toast-notification.service';
import { ViewportOverlayDirective } from '../../../../common/directives/viewport-overlay/viewport-overlay.directive';

const SUPPORT_RECIPIENT_EMAIL = 'hello@shoutugc.com';

@Component({
  selector: 'mb-contact-support-modal',
  imports: [FormField],
  hostDirectives: [ViewportOverlayDirective],
  templateUrl: './contact-support-modal.html',
  styleUrl: './contact-support-modal.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'closeModal()'
  }
})
export class ContactSupportModal {
  readonly close = output<void>();

  sendingRequest = signal<boolean>(false);
  requestSent = signal<boolean>(false);

  requestModel = signal({
    email: SUPPORT_RECIPIENT_EMAIL,
    message: '',
  });

  requestForm = form(this.requestModel, (schemaPath) => {
    required(schemaPath.email, { message: 'Email is required' });
    email(schemaPath.email, { message: 'Enter a valid email address' });
    disabled(schemaPath.email);
    required(schemaPath.message, { message: 'Message is required' });
  });

  private readonly currentUserService = inject(CurrentUserService);
  private readonly firebaseService = inject(FirebaseService);
  private readonly subscriptionService = inject(SubscriptionService);
  private readonly toastNotificationService = inject(ToastNotificationService);

  constructor() {
    void this.initFormDefaults();
  }

  private async initFormDefaults(): Promise<void> {
    const [firebaseUser, shoutUser] = await Promise.all([
      firstValueFrom(this.firebaseService.user$.pipe(take(1))),
      firstValueFrom(this.currentUserService.getShoutUser()),
    ]);

    const uid = firebaseUser?.uid ?? '(not available)';
    const firebaseEmail = firebaseUser?.email ?? '(not available)';
    const brandId = this.currentUserService.focussedBrand?.id ?? '(not available)';
    const firstName = shoutUser?.firstName ?? '(not available)';

    const message =
      'Hello SHOUT, \n\n' +
      'Please can you help me with: <...>\n\n' +
      `My User ID is: ${uid}\n` +
      `My Business Account ID is: ${brandId}\n` +
      `My email address is: ${firebaseEmail}\n\n` +
      'Thank you!\n' +
      `Regards: ${firstName}`;

    this.requestModel.set({
      email: SUPPORT_RECIPIENT_EMAIL,
      message,
    });
  }

  sendRequest(): void {
    if (this.requestForm().invalid()) {
      return;
    }
    this.sendingRequest.set(true);

    const to = this.requestModel().email.trim();
    const subject = 'SHOUT support request';
    const body = this.requestModel().message;

    this.subscriptionService.sendSupportRequest(to, subject, body).subscribe({
      next: () => {
        this.sendingRequest.set(false);
        this.requestSent.set(true);
      },
      error: () => {
        this.sendingRequest.set(false);
        void this.toastNotificationService.alert({
          title: 'Failed to send support request',
          message: 'Failed to send support request',
          type: 'error',
          icon: 'error'
        });
      }
    });
  }

  closeModal(): void {
    this.close.emit();
  }
}
